import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { QRCodeSVG } from 'qrcode.react'
import { useCart } from '../contexts/CartContext'
import { useAuth } from '../contexts/AuthContext'
import { useToast } from '../contexts/ToastContext'
import { localDb, formatCurrency, generateUPILink } from '../lib/db'
import { APP_CONFIG } from '../types'
import './Checkout.css'

export default function Checkout() {
    const navigate = useNavigate()
    const { items, getSubtotal, getSCoinsValue, getTotal, getSCoinsEarned, clearCart } = useCart()
    const { user, updateUser } = useAuth()
    const { showToast } = useToast()

    const [scoinsToUse, setScoinsToUse] = useState(0)
    const [paymentConfirmed, setPaymentConfirmed] = useState(false)
    const [pickupCode, setPickupCode] = useState('')
    const [isProcessing, setIsProcessing] = useState(false)

    const userScoins = user?.s_coins || 0
    const subtotal = getSubtotal()
    const scoinsDiscount = getSCoinsValue(scoinsToUse)
    const total = getTotal(scoinsToUse)
    const scoinsEarned = getSCoinsEarned(total)

    // Max S-Coins that can be used (in multiples of 100)
    const maxScoins = useMemo(() => {
        const maxAllowed = Math.floor(subtotal / APP_CONFIG.SCOINS_REDEEM_RATE) * 100
        return Math.min(userScoins, maxAllowed)
    }, [userScoins, subtotal])

    const upiLink = useMemo(() => {
        return generateUPILink(total, 'PENDING')
    }, [total])

    useEffect(() => {
        if (items.length === 0 && !pickupCode) {
            navigate('/cart')
        }
    }, [items.length, pickupCode, navigate])

    const handleConfirmPayment = async () => {
        if (!user) return

        setIsProcessing(true)

        try {
            // Create order
            const order = localDb.orders.create({
                user_id: user.id,
                items: items.map(({ product, quantity }) => ({
                    product_id: product.id,
                    product_name: product.name,
                    quantity,
                    price: product.sm_price
                })),
                total,
                s_coins_used: scoinsToUse,
                payment_method: 'upi'
            })

            // Deduct S-Coins if used
            if (scoinsToUse > 0) {
                localDb.scoins.redeem(user.id, scoinsToUse, order.id)
            }

            // Add earned S-Coins
            localDb.scoins.earn(user.id, scoinsEarned, order.id)

            // Update user's S-Coins balance
            const newBalance = userScoins - scoinsToUse + scoinsEarned
            updateUser({ s_coins: newBalance })

            // Set pickup code and mark as paid
            setPickupCode(order.pickup_code)
            localDb.orders.updateStatus(order.id, 'paid')

            // Clear cart
            clearCart()

            setPaymentConfirmed(true)
            showToast('Order placed successfully!', 'success')
        } catch (error) {
            showToast('Failed to process order. Please try again.', 'error')
        } finally {
            setIsProcessing(false)
        }
    }

    // Success Screen
    if (paymentConfirmed && pickupCode) {
        return (
            <div className="container">
                <div className="checkout-success">
                    <div className="success-icon">✓</div>
                    <h1>Order Confirmed!</h1>
                    <p>Thank you for your purchase</p>

                    <div className="pickup-code-card">
                        <span className="pickup-label">Your Pickup Code</span>
                        <div className="pickup-code">{pickupCode}</div>
                        <p className="pickup-instruction">
                            Show this code at <strong>SM Fashion, Nalgonda</strong> to collect your order
                        </p>
                    </div>

                    <div className="order-summary-success">
                        <div className="summary-item">
                            <span>Order Total</span>
                            <span>{formatCurrency(total)}</span>
                        </div>
                        {scoinsToUse > 0 && (
                            <div className="summary-item">
                                <span>S-Coins Used</span>
                                <span>-{scoinsToUse}</span>
                            </div>
                        )}
                        <div className="summary-item highlight">
                            <span>S-Coins Earned</span>
                            <span>+{scoinsEarned} 🪙</span>
                        </div>
                    </div>

                    <div className="success-actions">
                        <button onClick={() => navigate('/orders')} className="btn btn-primary btn-lg">
                            View My Orders
                        </button>
                        <button onClick={() => navigate('/')} className="btn btn-secondary btn-lg">
                            Continue Shopping
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="container">
            <div className="checkout-page">
                <h1 className="checkout-title">Checkout</h1>

                <div className="checkout-content">
                    {/* Payment Section */}
                    <div className="checkout-main">
                        {/* Order Items */}
                        <div className="checkout-section">
                            <h2>Order Items</h2>
                            <div className="checkout-items">
                                {items.map(({ product, quantity }) => (
                                    <div key={product.id} className="checkout-item">
                                        <div className="checkout-item-image">
                                            {product.image_url ? (
                                                <img src={product.image_url} alt={product.name} />
                                            ) : (
                                                <div className="item-placeholder">👗</div>
                                            )}
                                        </div>
                                        <div className="checkout-item-info">
                                            <span className="checkout-item-name">{product.name}</span>
                                            <span className="checkout-item-qty">Qty: {quantity}</span>
                                        </div>
                                        <span className="checkout-item-price">
                                            {formatCurrency(product.sm_price * quantity)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* S-Coins Section */}
                        {userScoins > 0 && (
                            <div className="checkout-section scoins-section">
                                <h2>🪙 Use S-Coins</h2>
                                <div className="scoins-balance">
                                    <span>Available Balance:</span>
                                    <span className="scoins-value">{userScoins} S-Coins</span>
                                </div>

                                <div className="scoins-slider-container">
                                    <input
                                        type="range"
                                        min="0"
                                        max={maxScoins}
                                        step="100"
                                        value={scoinsToUse}
                                        onChange={(e) => setScoinsToUse(Number(e.target.value))}
                                        className="scoins-slider"
                                    />
                                    <div className="scoins-slider-labels">
                                        <span>0</span>
                                        <span>{maxScoins}</span>
                                    </div>
                                </div>

                                {scoinsToUse > 0 && (
                                    <div className="scoins-discount-info">
                                        Using <strong>{scoinsToUse} S-Coins</strong> = {formatCurrency(scoinsDiscount)} discount
                                    </div>
                                )}
                            </div>
                        )}

                        {/* UPI Payment */}
                        <div className="checkout-section payment-section">
                            <h2>💳 Pay via UPI</h2>
                            <div className="payment-content">
                                <div className="qr-code-container">
                                    <div className="qr-code-wrapper">
                                        <QRCodeSVG
                                            value={upiLink}
                                            size={200}
                                            level="H"
                                            includeMargin
                                            bgColor="#ffffff"
                                            fgColor="#000000"
                                        />
                                    </div>
                                    <p className="qr-instruction">Scan with any UPI app</p>
                                </div>

                                <div className="payment-details">
                                    <div className="upi-id-display">
                                        <span className="upi-label">UPI ID:</span>
                                        <span className="upi-value">{APP_CONFIG.UPI_ID}</span>
                                    </div>

                                    <div className="amount-display">
                                        <span className="amount-label">Amount to Pay:</span>
                                        <span className="amount-value">{formatCurrency(total)}</span>
                                    </div>

                                    <button
                                        className="btn btn-primary btn-lg confirm-payment-btn"
                                        onClick={handleConfirmPayment}
                                        disabled={isProcessing}
                                    >
                                        {isProcessing ? (
                                            <span className="btn-loading">
                                                <span className="loader loader-sm"></span>
                                                Processing...
                                            </span>
                                        ) : (
                                            'I have paid - Confirm Order'
                                        )}
                                    </button>

                                    <p className="payment-note">
                                        Click above after completing your UPI payment
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Summary Sidebar */}
                    <div className="checkout-sidebar">
                        <div className="checkout-summary">
                            <h3>Order Summary</h3>

                            <div className="summary-row">
                                <span>Subtotal</span>
                                <span>{formatCurrency(subtotal)}</span>
                            </div>

                            {scoinsToUse > 0 && (
                                <div className="summary-row discount">
                                    <span>S-Coins Discount</span>
                                    <span>-{formatCurrency(scoinsDiscount)}</span>
                                </div>
                            )}

                            <hr />

                            <div className="summary-row total">
                                <span>Total</span>
                                <span>{formatCurrency(total)}</span>
                            </div>

                            <div className="scoins-will-earn">
                                <span className="scoins-icon">🪙</span>
                                <span>You'll earn <strong>{scoinsEarned} S-Coins</strong></span>
                            </div>
                        </div>

                        {/* Pickup Info */}
                        <div className="pickup-reminder">
                            <div className="pickup-icon">🏪</div>
                            <h4>Pay & Pickup</h4>
                            <p>
                                Complete payment and collect your order at:<br />
                                <strong>SM Fashion, Nalgonda</strong>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
